import { isImageEmpty, isImageReady } from '@/ui/editor/image-readiness'
import { campaignsShowQueryKey, useCampaignsShow, useWelcomeScreensUpdate } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { ConformedCampaignLandingPage, conformLandingPage, useLandingPageEditor } from '../../ui'

/**
 * Simplified landing page editor hook for post-launch campaign editing.
 * Unlike useNewLandingPageEditor, this always sets isGenerating: false since
 * launched campaigns are never in a generation state.
 */
export function usePostLaunchLandingPageEditor(campaignId: string) {
	const campaign = useCampaignsShow(campaignId)
	const queryClient = useQueryClient()

	const welcomeScreen = campaign.data?.welcomeScreen

	const landingPage: ConformedCampaignLandingPage | undefined = welcomeScreen && welcomeScreen.id
		? conformLandingPage({
			id: welcomeScreen.id,
			title: welcomeScreen.title ?? '',
			subtitle: welcomeScreen.subtitle ?? '',
			body: welcomeScreen.description ?? '',
			imageId: welcomeScreen.image?.id ?? '',
			// `__ui` is the editor's own blob stored inside the screen's meta. The
			// spec types meta's values as unknown, so its shape is named once here.
			meta: (welcomeScreen.meta?.__ui as Record<string, unknown> | undefined) ?? {},
		})
		: undefined

	const imagesHistory = welcomeScreen?.imagesHistory?.filter((image) => !isImageEmpty(image)).map((image) => ({
		id: image.id,
		category: 'image' as const,
		collection: image.collectionName,
		name: image.name,
		fileName: image.fileName,
		humanReadableSize: image.humanReadableSize,
		createdAt: new Date(image.createdAt),
		url: image.url,
		width: image.width,
		height: image.height,
		isReady: isImageReady(image),
	}))

	const image = welcomeScreen?.image
		? {
			id: welcomeScreen.image.id,
			url: welcomeScreen.image.url,
			isReady: isImageReady(welcomeScreen.image),
			isEmpty: isImageEmpty(welcomeScreen.image),
		}
		: undefined

	const { mutateAsync: welcomeScreenMutationAsync, isPending: isSaving } = useWelcomeScreensUpdate({
		mutationKey: ['update-welcome-screen-post-launch'],
		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
	})

	const handleLandingPageChange = useCallback(
		async (value: ConformedCampaignLandingPage) => {
			if (!welcomeScreen) {
				throw new Error('Welcome screen not found')
			}

			await welcomeScreenMutationAsync({
				welcomeScreen: welcomeScreen.id!,
				data: {
					title: value.title,
					subtitle: value.subtitle,
					description: value.body,
					imageId: value.imageId,
					meta: {
						...(welcomeScreen.meta ?? {}),
						__ui: value.meta,
					},
				},
			})
		},
		[welcomeScreen, welcomeScreenMutationAsync],
	)

	useLandingPageEditor({
		landingPage,
		isPending: campaign.isPending,
		isGenerating: false, // Key difference: never generating for launched campaigns
		isToolExecuting: false,
		imagesHistory,
		image,
		onLandingPageChange: handleLandingPageChange,
		landingPageVersion: welcomeScreen?.updatedAt,
	})

	return {
		isPending: campaign.isPending,
		isSaving,
	}
}
